<template>
  <div
    v-click-outside="onClickOutside"
    class="top-nav-button account-menu"
    :class="classes"
  >
    <!-- Header item -->
    <div class="account-menu__wrapper v-centerer" @click="toggle">
      <div v-if="currentTeam && currentTeam.id > 0" class="account-menu__details">
        <div v-if="currentUser !== null" class="account-menu__details__user-name">
          {{ $t('fullNameFormat', currentUser) }}
        </div>
        <!-- If is user and smartphone, set legend and team name on same line -->
        <div v-if="$store.state.device.isSmartphoneLandscape && currentUser !== null">
          <span class="account-menu__details__team-name">
            {{ currentTeam.teamName }}
          </span>
        </div>
        <!-- Otherwise on two lines -->
        <div v-else>
          <div class="account-menu__details__team-name">
            {{ currentTeam.teamName }}
          </div>
        </div>
      </div>
      <!-- Team Icon or user thumbnail if connected as a user -->
      <UserThumbnail
        v-if="currentUser !== null"
        :value="currentUser.id"
        :all-users="[currentUser]"
        disabled
        fetch-data
        @click.native="toggle"
      />
      <Icon>{{ arrowDownThin }}</Icon>
    </div>

    <!-- Modal view -->
    <div
      v-if="currentTeam && currentTeam.id > 0"
      v-show="isOpen"
      class="account-menu__modal"
    >
      <div class="account-menu__modal__content">
        <!-- When connected as a team -->
        <div v-if="currentUser === null" class="account-menu__modal__user-details">
          <EditableLabel
            v-model="currentTeam.teamName"
            :legend="`${$t('message.TEAM')}${$t('message.TWO_DOTS')}`"
            disabled
          />
        </div>
        <!-- When connected as a user -->
        <div v-else class="account-menu__modal__user-details">
          <div class="account-menu__modal__field">
            <div class="legend legend--bottom-margin-small">
              {{ $t('message.NAME') }}
            </div>
            <div>{{ $t('fullNameFormat', currentUser) }}</div>
          </div>

          <div class="account-menu__modal__field">
            <div class="legend legend--bottom-margin-small">
              {{ $t('message.IDENTIFIER') }}
            </div>
            <div>{{ currentUser.username }}</div>
          </div>

          <EditableLabelRadio
            :key="currentTeamKey"
            :value="currentUser.lastUsedTeam || currentUser.teamId"
            :items="allTeams.filter(team => currentUser.teams.includes(team.id))"
            class="account-menu__modal__field clickable-hoverable"
            label-key="name"
            :legend="$t('message.CURRENT_TEAM')"
            modal-in-component
            @input="switchUserTeam"
          />

          <div class="account-menu__modal__field">
            {{ $tc('message.N_MEMBERS', nMembers, { nb: nMembers }) }}
          </div>
        </div>
        <!-- Options, accessibility -->
        <div class="account-menu__modal__sep" />
        <div class="account-menu__modal__option__container">
          <RouterLink v-if="currentUser !== null" to="/user" class="add-button account-menu__modal__option">
            <Icon>{{ personMedium }}</Icon>
            <span>{{ $t('message.MY_ACCOUNT') }}</span>
          </RouterLink>
          <button class="add-button account-menu__modal__option" @click="displayReleaseNotes">
            <Icon>{{ rocketMedium }}</Icon>
            <span>{{ $t('release.ALL_RELEASE_NOTES') }}</span>
          </button>
          <RouterLink to="/settings" class="add-button account-menu__modal__option">
            <Icon>{{ settingsMedium }}</Icon>
            <span>{{ $t('message.SETTINGS') }}</span>
          </RouterLink>
          <RouterLink
            v-if="currentUser && currentUser.roles.includes(ROLE_ADMIN)"
            to="/config/details"
            class="add-button account-menu__modal__option"
          >
            <Icon>{{ managementMedium }}</Icon>
            <span>{{ $t('message.CONFIG_ADMIN') }}</span>
          </RouterLink>
          <RouterLink
            v-if="(currentUser && currentUser.accessShizenUsersStats)
              || (currentTeam && currentTeam.accessShizenUsersStats)"
            to="/shizen-stats"
            class="add-button account-menu__modal__option"
          >
            <Icon>{{ gwStatsMedium }}</Icon>
            <span>{{ $t('message.SHIZEN_STATS') }}</span>
          </RouterLink>
          <div class="account-menu__modal__sep" />
          <button
            class="add-button account-menu__modal__option"
            @click="logout"
          >
            <Icon>{{ logoutMedium }}</Icon>
            <span>{{ $t('message.LOGOUT') }}</span>
          </button>
        </div>
      </div>

      <LoadingOverlay v-model="loading" />
    </div>

    <!-- All release notes -->
    <ReleaseNotes :displayed="displayAllNotes" @close="closeReleaseNotes" />

    <Modal :value="displayDataUsage" @input="$store.commit(HIDE_DATA_USAGE)">
      <div class="data-usage-summary">
        <div class="data-usage-summary__text">
          {{ $t('message.WELCOME_TO_SHIZEN') }}
        </div>
        <div class="data-usage-summary__text">
          {{ $t('message.DATA_USAGE_SUMMARY') }}
        </div>
        <div class="data-usage-summary__text">
          {{ $t('message.DATA_USAGE_MY_ACCOUNT') }}
        </div>
        <br>
        <RouterLink to="/user">
          <button class="action-btn" @click="$store.commit(HIDE_DATA_USAGE)">
            {{ $t('message.MY_ACCOUNT') }}
          </button>
        </RouterLink>
        <br>
        <button class="action-btn" @click="$store.commit(HIDE_DATA_USAGE)">
          {{ $t('message.OK') }}
        </button>
      </div>
    </Modal>

  </div>
</template>

<script>
/**
 * Account menu. Icon at the top of the page (header)
 * and modal view when the Icon is clicked.
 */

import { mapState } from 'vuex';
import { GET_TEAMS, HIDE_DATA_USAGE } from '@/store/mutation-types';
import {
  FETCH_MENU_CFG_PAGES,
  FETCH_TEAMS,
  LOGOUT,
  SWITCH_USER_TEAM,
} from '@/store/action-types';
import { FETCH_NAVIGATION_LIST } from '@/store/modules/navigation/action-types';
import { FETCH_NOTIFICATIONS } from '@/store/modules/notifications/action-types';
import { CLOSE_SOCKET, OPEN_SOCKET } from '@/store/modules/socket/action-types';
// Components
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import Icon from '@/components/icon.vue';
import LoadingOverlay from '@/components/loading-overlay.vue';
import Modal from '@/components/modal.vue';
import ReleaseNotes from '@/components/release-notes.vue';
import UserThumbnail from '@/components/user-thumbnail.vue';
// Utils & assets
import {
  arrowDownThin,
  gwStatsMedium,
  logoutMedium,
  managementMedium,
  personMedium,
  rocketMedium,
  settingsMedium,
  teamMedium,
} from '@/assets/icons';
import SPage from '@/utils/classes/SPage';
// Socket utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
import { calert } from '@/utils/main';
import { ROLE_ADMIN } from '@/utils/constants';
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';

export default {

  name: 'AccountMenu',

  components: {
    EditableLabel,
    EditableLabelRadio,
    Icon,
    LoadingOverlay,
    Modal,
    ReleaseNotes,
    UserThumbnail,
  },

  mixins: [mixinSocketHandler],

  data() {
    return {
      LOGOUT,
      HIDE_DATA_USAGE,
      ROLE_ADMIN,
      arrowDownThin,
      gwStatsMedium,
      logoutMedium,
      managementMedium,
      personMedium,
      rocketMedium,
      settingsMedium,
      teamMedium,
      isOpen: false,
      displayAllNotes: false,
      loading: false,
      currentTeamKey: 1,
    };
  },

  computed: {
    classes() {
      return { open: this.isOpen };
    },
    /** Object: Team from teams/get.php where id = currentTeam.id */
    stateCurrentTeamObject() {
      return this.allTeams.find(team => team.id === this.currentTeam.id);
    },
    /** Object: Team from teams/get.php where id = currentUser.team */
    stateUserTeamObject() {
      return this.currentUser
        ? this.allTeams.find(team => team.id === this.currentUser.team)
        : null;
    },
    /** Number: Number of team members */
    nMembers() {
      return this.stateCurrentTeamObject ? this.stateCurrentTeamObject.users.length : 0;
    },
    ...mapState({
      allTeams: state => state[GET_TEAMS.stateKey] || [],
      allTeamsLoading: state => state[GET_TEAMS.loadingKey],
      displayDataUsage: state => state.displayDataUsage,
    }),
  },

  watch: {
    $route() {
      this.onClickOutside();
    },
  },

  created() {
    this.handleSocketMessage(SocketMessages.USER_SWITCHED_TEAM, (socketData) => {
      this.$store.dispatch(SWITCH_USER_TEAM, {
        team: socketData.teamId,
        noSocketMessage: true,
      });

      calert(this.$t('message.ACCOUNT_SWITCHED_TEAM'), [
        {
          title: this.$t('message.ACCOUNT_SWITCH_REFRESH_NOW'),
          action: () => {
            this.refreshAfterSwitch();
          },
        },
        {
          title: this.$t('message.ACCOUNT_SWITCH_REFRESH_LATER'),
        },
      ]);
    });
  },

  methods: {
    /** Hide panel when clicking anywhere */
    onClickOutside() {
      if (this.isOpen) {
        this.currentTeamKey++;
        this.toggle();
      }
    },
    /** Open or close the panel */
    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        if (!this.allTeamsLoading) {
          this.loading = true;
          this.$store.dispatch(FETCH_TEAMS)
            .then(() => {
              this.loading = false;
            })
            .catch(() => {
              this.loading = false;
            });
        }
      }
    },
    /** Release notes button clicked */
    displayReleaseNotes() {
      if (this.isOpen) {
        this.toggle();
      }
      this.displayAllNotes = true;
    },
    closeReleaseNotes(clickEvt) {
      if (clickEvt) {
        clickEvt.stopPropagation();
      }
      this.displayAllNotes = false;
    },
    /** Switch between teams and login as another team */
    switchUserTeam(team) {
      this.loading = true;
      this.$store.dispatch(SWITCH_USER_TEAM, { team }).then(() => {
        this.$nextTick(() => {
          this.loading = false;
          this.refreshAfterSwitch();
        });
      });
    },
    /** Refresh the most basic data after switching to a different team */
    refreshAfterSwitch() {
      // Refresh the menu
      this.$store.dispatch(`navigation/${FETCH_NAVIGATION_LIST}`);
      this.$store.dispatch(FETCH_MENU_CFG_PAGES);
      // Restart the socket (newauth)
      this.$store.dispatch(`socket/${CLOSE_SOCKET}`).then(() => {
        this.$store.dispatch(`socket/${OPEN_SOCKET}`);
      });
      // Refresh the notifications
      this.$store.dispatch(`notifications/${FETCH_NOTIFICATIONS}`);
      // Refresh the content of the page
      // Resetting the page needs to be in a next tick as some pages must process the team change
      // To edit the query params (eg: gw/templates.vue)
      this.$nextTick(() => {
        const { path, fullPath } = this.$router.currentRoute;
        const specPageIds = [SPage.DETAILED_INDICATOR.id, SPage.INDICATOR_INPUT.id, SPage.CFG_PAGE.id];
        if (SPage.fromLink[path] && SPage.fromLink[path].id && specPageIds.includes(SPage.fromLink[path].id)) {
          // If the page is a secific team indicator or a configurable page, go back to home page
          globalEventDispatcher.dispatch(ShizenGlobalEvent.PUSH_HOME_PAGE);
        } else {
          // Otherwise, just reset the page
          const replacement = fullPath.includes('/dashboard') ? '/pending' : '/dashboard';
          this.$router.replace(replacement);
          this.$nextTick(() => {
            this.$router.replace(fullPath);
          });
        }
      });
    },
    logout() {
      this.$store.dispatch(LOGOUT, { app: this });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.nav-header .account-menu.top-nav-button {
  position: relative;
  width: auto;
  max-width: 20em;
  box-sizing: border-box;
}

.account-menu {
  padding: 0;

  &.open {
    background: $color-gray-background;
  }

  &__wrapper {
    display: flex;
    align-items: center;

    max-width: 100%;
    overflow: hidden;

    > img {
      margin-left: 0.5em;
    }

    .icon {
      flex: 0 0 auto;
      width: 1.4rem;
      height: 1.4rem;
    }

    .user-thumbnail {
      width: 2.3em;
      height: 2.3em;
    }
  }

  &__details {
    flex: 1;
    margin-right: .5rem;
    text-align: center;
    min-width: 0px;

    > div {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }

    &__team-legend {
      font-style: italic;
      font-weight: 300;
    }

    &__team-name {
      text-align: right;
    }

    &__user-name {
      text-align: right;
      font-weight: 600;
    }
  }

  &__modal {
    $side-padding: 0.5rem;

    position: absolute;
    right: 0;
    width: 220px;
    top: calc(100% + 0.5em);
    z-index: 10018;
    background-color: #fff;
    border-radius: $main-border-radius;
    cursor: default;
    padding: 0.7em $side-padding;
    box-shadow: $light-box-shadow;

    &__content {
      width: 100%;
    }

    &__user-details {
      padding: 0 .5rem;
    }

    &__field {
      padding: 0.5em;
      border-radius: $high-border-radius;

      &.editable-label .legend.legend--bottom-margin {
        margin-bottom: 0.3rem;
      }
    }

    &__option {
      padding: 0.7em 1em;
      border-radius: $high-border-radius;

      &__container {
        width: 100%;
        display: flex;
        flex-direction: column;
      }

      &:hover {
        background-color: $color-shizen-card-hover;
      }
    }

    &__sep {
      height: 1px;
      width: calc(100% + calc(2 * #{$side-padding}));
      background: $color-gray-background;
      margin: 1rem #{-$side-padding};
    }
  }

  .data-usage-summary {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-flow: column wrap;
    padding: 2em;
    cursor: initial;
    box-sizing: border-box;

    &__text:not(:first-child) {
      margin-top: 1em;
      max-width: 100%;
      white-space: normal;
      text-align: center;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .account-menu {
    &__wrapper {
      .user-thumbnail {
        width: 2.1em;
        height: 2.1em;
      }
    }

    &__details {
      display: none;
    }
  }
}
</style>
